import type { CreateBroadsheetTemplateInput, ResultTemplate } from '@/types/composition-functions';
import type { PdfHeaderConfig } from '@/types/wizardCommon';

export type { PdfHeaderConfig };

/**
 * Pure logic for the conversational broadsheet-template wizard.
 * Mirrors resultTemplateWizard.ts: payload composition stays free of
 * Vue/Apollo so it can be unit tested.
 */

export interface BroadsheetSourceKeyOption {
  key: string;
  label: string;
  /** Names of the result templates this key appears in (for the chip tooltip). */
  templateNames: string[];
  /**
   * 'score' keys (assessments, totals) are what most broadsheets need and
   * are shown up-front; 'derived' keys (grades, GPA plumbing) hide behind
   * a "more options" toggle so first-time users aren't overwhelmed.
   */
  kind: 'score' | 'derived';
}

/** Result-template column types whose values are scores rather than derived data. */
const SCORE_COLUMN_TYPES = ['ASSESSMENT', 'CALCULATION', 'HISTORICAL'];

export interface BroadsheetStudentInfoOption {
  key: string;
  label: string;
  tertiaryLabel?: string;
  tertiaryOnly?: boolean;
}

export type BroadsheetPositionChoice = 'sum' | 'average' | 'none';

export interface BroadsheetWizardAnswers {
  name: string;
  /** Result-template column keys shown under each subject, in order. */
  sourceKeys: BroadsheetSourceKeyOption[];
  /** STUDENT_INFO keys, in order. */
  studentInfoKeys: string[];
  includePercentage: boolean;
  positionChoice: BroadsheetPositionChoice;
  useCourseAbbreviation: boolean;
  showUnitInHeaders: boolean;
  showStatusInHeaders: boolean;
  showCourseLegend: boolean;
  showGradeLegend: boolean;
  showSignatures: boolean;
  signatureTitles: string[];
  pdfHeader: PdfHeaderConfig;
}

// PdfHeaderConfig imported from @/types/wizardCommon

export interface BroadsheetColumnDraft {
  label: string;
  key: string;
  type: string;
  calculation_function?: string | null;
  calculation_arguments?: unknown;
}

export const STUDENT_INFO_OPTIONS: BroadsheetStudentInfoOption[] = [
  { key: 'sn', label: 'Serial No', tertiaryLabel: 'S/N' },
  { key: 'matric_no', label: 'Admission / Matric No', tertiaryLabel: 'Matric No' },
  { key: 'student_name', label: 'Full Name', tertiaryLabel: 'Name' },
  { key: 'campus', label: 'Campus' },
  { key: 'tur', label: 'Total Units Registered', tertiaryLabel: 'TUR', tertiaryOnly: true },
];

export const DEFAULT_STUDENT_INFO_KEYS = ['student_name', 'matric_no'];
export const DEFAULT_TERTIARY_STUDENT_INFO_KEYS = ['matric_no', 'student_name', 'tur'];

/**
 * Union of column keys across the school's result templates, so the wizard
 * can offer real choices instead of asking users to type source keys.
 * Keys keep the order of first appearance; `total` is bumped to the front
 * because it is the overwhelmingly common pick.
 */
export function collectSourceKeyOptions(
  templates: Array<Pick<ResultTemplate, 'name'> & { columns: Array<{ key: string; label: string; type?: string }> }>,
): BroadsheetSourceKeyOption[] {
  const byKey = new Map<string, BroadsheetSourceKeyOption>();
  templates.forEach((template) => {
    (template.columns || []).forEach((column) => {
      const existing = byKey.get(column.key);
      if (existing) {
        if (!existing.templateNames.includes(template.name)) existing.templateNames.push(template.name);
      } else {
        byKey.set(column.key, {
          key: column.key,
          label: column.label,
          templateNames: [template.name],
          kind: !column.type || SCORE_COLUMN_TYPES.includes(column.type) ? 'score' : 'derived',
        });
      }
    });
  });

  // Score keys before derived ones, with total leading the pack.
  const options = Array.from(byKey.values());
  const scores = options.filter((o) => o.kind === 'score');
  const derived = options.filter((o) => o.kind === 'derived');
  const totalIndex = scores.findIndex((o) => o.key === 'total');
  if (totalIndex > 0) {
    const [total] = scores.splice(totalIndex, 1);
    if (total) scores.unshift(total);
  }
  return [...scores, ...derived];
}

/**
 * Selected keys that exist in SOME but not ALL of the school's result
 * templates — classes whose template lacks the key would show blanks, so
 * the wizard warns about these explicitly instead of leaving the user to
 * discover empty columns on a generated report.
 */
export function partialCoverageSelections(
  selectedKeys: string[],
  options: BroadsheetSourceKeyOption[],
  templateCount: number,
): BroadsheetSourceKeyOption[] {
  if (templateCount <= 1) return [];
  return options.filter(
    (option) => selectedKeys.includes(option.key)
      && option.templateNames.length > 0
      && option.templateNames.length < templateCount,
  );
}

/** The numeric key percentage/position calculations read from. */
export function primaryScoreKey(answers: BroadsheetWizardAnswers): string {
  const totalPick = answers.sourceKeys.find((s) => s.key === 'total');
  if (totalPick) return totalPick.key;
  return answers.sourceKeys[0]?.key || 'total';
}

export function buildBroadsheetColumnDrafts(
  answers: BroadsheetWizardAnswers,
  isTertiary: boolean,
): BroadsheetColumnDraft[] {
  const drafts: BroadsheetColumnDraft[] = [];

  answers.studentInfoKeys.forEach((key) => {
    const option = STUDENT_INFO_OPTIONS.find((o) => o.key === key);
    if (!option || (option.tertiaryOnly && !isTertiary)) return;
    const label = isTertiary && option.tertiaryLabel ? option.tertiaryLabel : option.label;
    drafts.push({ label, key: option.key, type: 'STUDENT_INFO' });
  });

  answers.sourceKeys.forEach((source, index) => {
    drafts.push({
      label: answers.sourceKeys.length === 1
        ? (isTertiary ? 'Courses' : 'Subjects')
        : source.label,
      key: index === 0 ? (isTertiary ? 'courses' : 'subjects') : `subjects_${source.key}`,
      type: 'DYNAMIC_SUBJECTS',
      calculation_arguments: { source_key: source.key },
    });
  });

  const scoreKey = primaryScoreKey(answers);

  if (answers.includePercentage) {
    drafts.push({
      label: 'Percentage',
      key: 'percentage',
      type: 'STUDENT_PERCENTAGE',
      calculation_arguments: { source_key: scoreKey, max_score: 100 },
    });
  }

  if (answers.positionChoice !== 'none') {
    drafts.push({
      label: 'Position',
      key: 'position',
      type: 'STUDENT_POSITION',
      calculation_arguments: { source_key: scoreKey, aggregate: answers.positionChoice },
    });
  }

  return drafts;
}

export function buildCreateBroadsheetInput(
  answers: BroadsheetWizardAnswers,
  isTertiary: boolean,
): CreateBroadsheetTemplateInput {
  const drafts = buildBroadsheetColumnDrafts(answers, isTertiary);

  return {
    name: answers.name.trim(),
    description: `Created with the guided setup — scores: ${answers.sourceKeys.map((s) => s.label).join(', ')}`,
    properties: {
      use_course_abbreviation_in_headers: answers.useCourseAbbreviation,
      show_unit_in_headers: answers.showUnitInHeaders,
      show_status_in_headers: answers.showStatusInHeaders,
      show_course_legend: answers.showCourseLegend,
      show_grade_legend: answers.showGradeLegend,
      show_signatures: answers.showSignatures,
      signatures: answers.showSignatures && answers.signatureTitles.length > 0
        ? answers.signatureTitles.map((title) => ({ title, show_sign: true, show_date: true }))
        : null,
      pdf_header: {
        school_name: answers.pdfHeader.schoolName || null,
        report_title: answers.pdfHeader.reportTitle || null,
        semester_format: answers.pdfHeader.semesterFormat || null,
        logo_position: answers.pdfHeader.logoPosition || null,
        level_display_map: Object.keys(answers.pdfHeader.levelDisplayMap || {}).length > 0
          ? answers.pdfHeader.levelDisplayMap
          : null,
      },
    },
    columns: drafts.map((draft, index) => ({
      label: draft.label,
      key: draft.key,
      type: draft.type,
      sequence: index + 1,
      is_visible: true,
      calculation_function: draft.calculation_function ?? null,
      calculation_arguments: draft.calculation_arguments ?? null,
    })),
  };
}

// --- Live preview ----------------------------------------------------------

export interface BroadsheetPreviewStudent {
  name: string;
  matric: string;
  /** Per-subject base score ratios. */
  ratios: number[];
  unit: number;
}

export const PREVIEW_STUDENTS: BroadsheetPreviewStudent[] = [
  { name: 'Adaeze Okafor', matric: 'STU/001', ratios: [0.88, 0.74, 0.81], unit: 18 },
  { name: 'Ibrahim Musa', matric: 'STU/002', ratios: [0.66, 0.79, 0.58], unit: 16 },
  { name: 'Chioma Eze', matric: 'STU/003', ratios: [0.72, 0.61, 0.69], unit: 18 },
];

export const PREVIEW_SUBJECTS = ['Mathematics', 'English', 'Basic Sci.'];
export const PREVIEW_COURSES = ['CSC 101', 'MTH 102', 'GST 103'];

export interface BroadsheetPreviewRow {
  info: Record<string, string | number>;
  /** subjects[subjectIndex][sourceKeyIndex] — value per selected source key. */
  subjects: Array<Array<string | number>>;
  percentage: string;
  position: number;
}

/**
 * Sample rows for the live preview. Scores are derived from each student's
 * per-subject ratios; percentage and position are genuinely computed so the
 * preview behaves like the real broadsheet.
 */
export function buildBroadsheetPreviewRows(answers: BroadsheetWizardAnswers): BroadsheetPreviewRow[] {
  const rows = PREVIEW_STUDENTS.map((student, studentIndex) => {
    const subjects = PREVIEW_SUBJECTS.map((_, subjectIndex) => {
      const ratio = student.ratios[subjectIndex] ?? 0.5;
      const total = Math.round(ratio * 100);
      return answers.sourceKeys.map((source) => {
        // Approximate per-key sample values from the subject total.
        if (source.key === 'total' || answers.sourceKeys.length === 1) return total;
        if (/exam/i.test(source.key)) return Math.round(total * 0.6);
        if (/grade|remark/i.test(source.key)) return total >= 70 ? 'A' : total >= 60 ? 'B' : total >= 50 ? 'C' : 'D';
        return Math.round(total * 0.4);
      });
    });

    const totals = student.ratios.map((r) => Math.round(r * 100));
    const sum = totals.reduce((a, b) => a + b, 0);
    const percentage = (sum / (PREVIEW_SUBJECTS.length * 100)) * 100;

    return {
      info: {
        sn: studentIndex + 1,
        student_name: student.name,
        matric_no: student.matric,
        tur: student.unit,
      },
      subjects,
      percentage: percentage.toFixed(1),
      position: 0, // filled below
      sum,
    };
  });

  // Rank by sum (or average — identical ordering for equal subject counts).
  const ranked = [...rows].sort((a, b) => b.sum - a.sum);
  rows.forEach((row) => {
    row.position = ranked.indexOf(row) + 1;
  });

  return rows.map(({ sum: _sum, ...row }) => row);
}
